import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { collection, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { App, Button, Empty, Popconfirm, Spin } from "antd";
import {
  BOOKING_TYPE_LABEL,
  COLLECTIONS,
  type Booking,
  type BookingManifest,
  type CancelBookingResponse,
  type StartBookingPaymentResponse,
} from "@gull/shared";
import { db } from "../../lib/firebase";
import { enduserApi, ApiError } from "../../lib/api";

export function BookingsTab({ uid }: { uid: string }) {
  const [bookings, setBookings] = useState<Booking[] | null>(null);
  const [manifestNames, setManifestNames] = useState<Record<string, string>>({});
  useEffect(() => {
    const q = query(
      collection(db, COLLECTIONS.bookings),
      where("customerUid", "==", uid),
      orderBy("scheduledAt", "desc"),
    );
    return onSnapshot(q, (snap) => setBookings(snap.docs.map((d) => d.data() as Booking)));
  }, [uid]);
  useEffect(() => {
    const manifestQuery = query(
      collection(db, COLLECTIONS.bookingManifests),
      where("active", "==", true),
    );
    return onSnapshot(manifestQuery, (snap) => {
      const next: Record<string, string> = {};
      snap.docs.forEach((d) => {
        const manifest = d.data() as BookingManifest;
        next[manifest.id] = manifest.name;
      });
      setManifestNames(next);
    });
  }, []);

  const split = useMemo(() => {
    if (!bookings) return null;
    const now = Date.now();
    const upcoming: Booking[] = [];
    const past: Booking[] = [];
    for (const b of bookings) {
      if (
        b.scheduledAt >= now &&
        b.status !== "CANCELLED" &&
        b.status !== "COMPLETED" &&
        b.status !== "NO_SHOW"
      ) {
        upcoming.push(b);
      } else {
        past.push(b);
      }
    }
    upcoming.sort((a, b) => a.scheduledAt - b.scheduledAt);
    return { upcoming, past };
  }, [bookings]);

  if (split === null) return <Spin />;
  if (split.upcoming.length === 0 && split.past.length === 0) {
    return (
      <div className="minside-empty-panel">
        <Empty image={null} description="Ingen bookinger ennå.">
          <Link to="/book">
            <Button type="primary">Book en time</Button>
          </Link>
        </Empty>
      </div>
    );
  }
  return (
    <div className="minside-section-stack">
      <section className="minside-booking-group">
        <div className="minside-kicker">Kommende timer</div>
        {split.upcoming.length === 0 ? (
          <div className="minside-empty-panel minside-empty-panel--compact">
            <Empty image={null} description="Ingen kommende bookinger.">
              <Link to="/book">
                <Button type="primary">Book en time</Button>
              </Link>
            </Empty>
          </div>
        ) : (
          <div className="minside-card-list">
            {split.upcoming.map((b) => (
              <BookingCard
                key={b.id}
                booking={b}
                cancellable
                locationName={manifestNames[b.manifestId] ?? "David-Andersen"}
              />
            ))}
          </div>
        )}
      </section>
      {split.past.length > 0 ? (
        <section className="minside-booking-group">
          <div className="minside-kicker">Tidligere timer</div>
          <div className="minside-card-list">
            {split.past.map((b) => (
              <BookingCard
                key={b.id}
                booking={b}
                cancellable={false}
                locationName={manifestNames[b.manifestId] ?? "David-Andersen"}
              />
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

function BookingCard({
  booking,
  cancellable,
  locationName,
}: {
  booking: Booking;
  cancellable: boolean;
  locationName: string;
}) {
  const { message } = App.useApp();
  const [cancelling, setCancelling] = useState(false);
  const [paying, setPaying] = useState(false);

  const cancel = async () => {
    setCancelling(true);
    try {
      await enduserApi.authedPost<CancelBookingResponse>(`/bookings/${booking.id}/cancel`, {});
      message.success("Bestilling avbestilt");
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : "Kunne ikke avbestille");
    } finally {
      setCancelling(false);
    }
  };

  const pay = async () => {
    setPaying(true);
    try {
      if (booking.payment.checkoutUrl) {
        window.location.assign(booking.payment.checkoutUrl);
        return;
      }
      const session = await enduserApi.authedPost<StartBookingPaymentResponse>(
        `/bookings/${booking.id}/payment/start`,
        {},
      );
      window.location.assign(session.checkoutUrl);
    } catch (err) {
      if (err instanceof ApiError && err.code === "DINTERO_NOT_CONFIGURED") {
        message.info("Betaling er ikke aktivert ennå");
      } else {
        message.error(err instanceof ApiError ? err.message : "Kunne ikke starte betaling");
      }
    } finally {
      setPaying(false);
    }
  };

  const paymentStatus = booking.payment?.status ?? "NONE";
  const showPayButton =
    cancellable &&
    (paymentStatus === "REQUIRED" || paymentStatus === "FAILED" || paymentStatus === "PENDING") &&
    (booking.payment?.amount ?? 0) > 0;

  const date = new Date(booking.scheduledAt);
  const dateLabel = date.toLocaleDateString("nb-NO", {
    timeZone: "Europe/Oslo",
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
  const timeLabel = date.toLocaleTimeString("nb-NO", {
    timeZone: "Europe/Oslo",
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <article className="minside-booking-card">
      <div className="minside-booking-card__top">
        <div>
          <h2>{BOOKING_TYPE_LABEL[booking.type]}</h2>
          {booking.notes ? <p className="minside-booking-card__notes">{booking.notes}</p> : null}
          {(booking.preRegistrationItems ?? []).length > 0 ? (
            <div className="minside-booking-card__items">
              <span>Forhåndsregistrert</span>
              <div>
                {(booking.preRegistrationItems ?? []).map((item, index) => (
                  <p key={item.id}>
                    {item.description || `Gjenstand ${index + 1}`}
                    {[
                      item.estimatedKarat ? `${item.estimatedKarat} kt` : null,
                      item.estimatedWeightGrams != null ? `${item.estimatedWeightGrams} g` : null,
                      item.notes,
                    ]
                      .filter(Boolean)
                      .map((value, valueIndex) => (valueIndex === 0 ? ` (${value}` : ` · ${value}`))
                      .join("")}
                    {[item.estimatedKarat, item.estimatedWeightGrams, item.notes].some(
                      (value) => value != null && value !== "",
                    )
                      ? ")"
                      : ""}
                  </p>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        <div className="minside-booking-meta">
          <div>
            <span>Dato</span>
            <strong>{dateLabel}</strong>
          </div>
          <div>
            <span>Lokasjon</span>
            <strong>{locationName}</strong>
          </div>
          <div>
            <span>Klokkeslett</span>
            <strong>{timeLabel}</strong>
          </div>
          <div>
            <span>Varighet</span>
            <strong>{booking.durationMinutes} min</strong>
          </div>
          <span className="minside-card-chevron" aria-hidden="true">
            ⌃
          </span>
        </div>
      </div>

      {showPayButton ||
      (cancellable && (booking.status === "PENDING" || booking.status === "CONFIRMED")) ? (
        <div className="minside-booking-actions">
          {showPayButton ? (
            <Button type="primary" loading={paying} onClick={() => void pay()}>
              {paymentStatus === "FAILED" ? "Prøv igjen" : "Betal nå"}
            </Button>
          ) : null}
          {cancellable && (booking.status === "PENDING" || booking.status === "CONFIRMED") ? (
            <Popconfirm
              title="Avbestill?"
              description="Du kan bestille en ny time når som helst."
              okText="Avbestill"
              okButtonProps={{ danger: true }}
              cancelText="Avbryt"
              onConfirm={() => void cancel()}
            >
              <Button danger loading={cancelling}>
                Avbestill
              </Button>
            </Popconfirm>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}
